if (!customElements.get('tora-offers-landing')) {
  customElements.define(
    'tora-offers-landing',
    class ToraOffersLanding extends HTMLElement {
      connectedCallback() {
        this.version = this.dataset.landingVersion || 'tora_offers_v6_concise_copy';
        this.currency = this.dataset.currency || 'SAR';
        this.locale = this.dataset.locale || 'ar-SA';
        this.isArabic = this.getAttribute('dir') === 'rtl';
        this.abortController = new AbortController();
        this.activeCard = this.querySelector('[data-tora-product-card]:not([hidden])');
        this.activeSheet = null;
        this.activeSheetTrigger = null;
        this.topProductsPassed = false;
        this.keyboardOpen = false;
        this.pendingAdds = new Map();
        this.pendingTimers = new Map();
        this.floatingWidgets = new Map();
        this.sessionAddedVariants = new Set();
        this.cartStateRevision = 0;
        this.cartDrawerNeedsRefresh = false;
        this.cartSnapshotTimer = null;
        this.openingCart = false;

        this.sticky = this.querySelector('[data-tora-sticky]');
        this.stickyButton = this.querySelector('[data-tora-sticky-add]');
        this.stickyAddLabel = this.stickyButton?.textContent.trim() || '';
        this.stickyAddAriaLabel = this.stickyButton?.getAttribute('aria-label');
        this.stickyTitle = this.querySelector('[data-tora-sticky-title]');
        this.stickyPrice = this.querySelector('[data-tora-sticky-price]');
        this.topProductsEnd = this.querySelector('[data-tora-top-products-end]');
        this.announcement = this.querySelector('[data-tora-announcement]');
        this.cartDrawer = document.querySelector('cart-drawer');
        this.prepareNativeCartTarget();

        this.querySelectorAll('[data-tora-add-button]').forEach((button) => {
          const label = button.querySelector('span');
          if (label) button.dataset.defaultFullLabel = label.textContent.trim();
        });

        this.updateStickyProduct(this.activeCard);
        this.retargetHeaderCtas();
        this.prepareHeaderCartLinks();
        if (this.cartDrawer) {
          customElements.whenDefined('cart-drawer').then(() => {
            if (this.isConnected) this.prepareHeaderCartLinks();
          });
        }

        this.addEventListener('click', this.onClick.bind(this), { signal: this.abortController.signal });
        this.addEventListener('change', this.onChange.bind(this), { signal: this.abortController.signal });
        this.addEventListener('submit', this.onSubmit.bind(this), {
          capture: true,
          signal: this.abortController.signal,
        });
        document.addEventListener('keydown', this.onDocumentKeydown.bind(this), {
          signal: this.abortController.signal,
        });
        document.addEventListener('click', this.onHeaderCartClick.bind(this), {
          signal: this.abortController.signal,
        });
        ['input', 'change', 'click'].forEach((eventName) => {
          document.addEventListener(eventName, this.onCartInteraction.bind(this), {
            capture: true,
            signal: this.abortController.signal,
          });
        });
        document.addEventListener('shopify:cart:error', this.onStandardCartError.bind(this), {
          signal: this.abortController.signal,
        });
        window.addEventListener('pageshow', this.onPageShow.bind(this), { signal: this.abortController.signal });

        if (window.visualViewport) {
          window.visualViewport.addEventListener('resize', this.onViewportResize.bind(this), {
            signal: this.abortController.signal,
          });
        }

        this.observeTopProducts();
        this.observeCartDrawer();
        this.observeFloatingWidgets();
        this.subscribeToCartEvents();
        this.syncStickyVisibility();
      }

      disconnectedCallback() {
        this.abortController?.abort();
        this.cartReadController?.abort();
        window.clearTimeout(this.cartSnapshotTimer);
        this.topObserver?.disconnect();
        this.drawerObserver?.disconnect();
        this.cartDrawer?.querySelector('[data-tora-cart-assurance-content]')?.remove();
        this.nativeCartTarget?.remove();
        this.nativeCartTarget = null;
        this.widgetObserver?.disconnect();
        this.unsubscribeCartUpdate?.();
        this.unsubscribeCartError?.();
        this.pendingTimers.forEach((timer) => window.clearTimeout(timer));
        this.pendingTimers.clear();
        window.clearTimeout(this.widgetTimer);
        document.body.classList.remove('tora-options-open', 'tora-products-passed');
        this.restoreFloatingWidgets();
      }

      onPageShow(event) {
        this.closeSheet(false);
        this.syncStickyVisibility();
        if (!event?.persisted) return;
        this.invalidateCartRead();
        this.refreshCartState({ includeDrawer: true });
      }

      onViewportResize() {
        const viewport = window.visualViewport;
        this.keyboardOpen = Boolean(viewport && window.innerHeight - viewport.height > 150);
        this.syncStickyVisibility();
      }

      onClick(event) {
        const closeTrigger = event.target.closest('[data-tora-options-close]');
        if (closeTrigger) {
          this.closeSheet();
          return;
        }

        const optionsTrigger = event.target.closest('[data-tora-options-trigger]');
        if (optionsTrigger) {
          const card = optionsTrigger.closest('[data-tora-product-card]');
          this.selectCard(card, 'card');
          const sheet = card?.querySelector('[data-tora-options-sheet]');
          if (sheet) this.openSheet(sheet, optionsTrigger);
          return;
        }

        const stickyAdd = event.target.closest('[data-tora-sticky-add]');
        if (stickyAdd) {
          if (this.activeVariantWasAdded()) {
            this.openCart(stickyAdd);
            return;
          }
          if (this.submitActiveCard(stickyAdd)) {
            this.track('tora_sticky_add_to_cart', this.cardPayload(this.activeCard, 'sticky'));
          }
          return;
        }

        const addButton = event.target.closest('[data-tora-add-button]');
        if (addButton) {
          const card = addButton.closest('[data-tora-product-card]');
          this.selectCard(card, 'card');
          return;
        }

        const cardLink = event.target.closest('[data-tora-card-link]');
        if (cardLink) {
          const card = cardLink.closest('[data-tora-product-card]');
          this.selectCard(card, 'card');
        }
      }

      onChange(event) {
        const radio = event.target.closest('[data-tora-sheet-variant]');
        if (!radio) return;
        const automaticSelection = radio.dataset.toraAutomaticSelection === 'true';
        delete radio.dataset.toraAutomaticSelection;

        const sheet = radio.closest('[data-tora-options-sheet]');
        const card = radio.closest('[data-tora-product-card]');
        const hiddenVariant = sheet?.querySelector('[data-tora-sheet-variant-id]');
        const price = Number(radio.dataset.price || 0);
        const priceFormatted = this.formatMoney(price);

        if (hiddenVariant) hiddenVariant.value = radio.value;
        if (card) {
          card.dataset.variantId = radio.value;
          card.dataset.price = String(price);
          card.dataset.priceFormatted = priceFormatted;
          const priceNode = card.querySelector('.tora-card__price');
          if (priceNode) priceNode.textContent = priceFormatted;
        }

        const submit = sheet?.querySelector('[data-tora-add-button]');
        const submitLabel = submit?.querySelector('span');
        if (submit && submitLabel) {
          const baseLabel = submit.dataset.defaultLabel || '';
          submitLabel.textContent = `${baseLabel} — ${priceFormatted}`;
          submit.dataset.defaultFullLabel = submitLabel.textContent.trim();
        }

        this.updateStickyProduct(card);
        if (!automaticSelection) {
          this.announce(
            this.isArabic
              ? `تم اختيار ${radio.closest('label')?.innerText.trim() || ''}`
              : `Selected ${radio.closest('label')?.innerText.trim() || ''}`
          );
          this.track('tora_offer_selected', this.cardPayload(card, sheet?.dataset.toraOpenSource || 'card'));
        }
      }

      onSubmit(event) {
        const form = event.target.closest('[data-tora-card-form]');
        if (!form) return;

        const button = form.querySelector('[data-tora-add-button]');
        if (button?.getAttribute('aria-disabled') === 'true' || button?.disabled) return;

        const card = form.closest('[data-tora-product-card]');
        const sheet = form.closest('[data-tora-options-sheet]');
        const source = form.dataset.toraSubmitSource || sheet?.dataset.toraOpenSource || 'card';
        delete form.dataset.toraSubmitSource;
        const variant = String(form.querySelector('[name="id"]')?.value || card?.dataset.variantId || '');
        if (!variant) return;
        if (this.pendingAdds.has(variant)) {
          event.preventDefault();
          event.stopImmediatePropagation();
          return;
        }

        this.updateStickyProduct(card);
        this.invalidateCartRead();
        this.pendingAdds.set(variant, { form, card, button, source });
        this.track('tora_quick_add', this.cardPayload(card, source));

        const timeout = window.setTimeout(() => {
          const pending = this.pendingAdds.get(variant);
          if (!pending) return;
          pending.timedOut = true;
          this.announce(
            this.isArabic
              ? 'الإضافة ما زالت قيد المعالجة. تحقق من السلة قبل إعادة المحاولة.'
              : 'The add is still processing. Check the cart before trying again.'
          );
        }, 20000);
        this.pendingTimers.set(variant, timeout);
      }

      subscribeToCartEvents() {
        if (typeof subscribe !== 'function' || typeof PUB_SUB_EVENTS === 'undefined') return;

        this.unsubscribeCartUpdate = subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
          this.invalidateCartRead();
          if (event?.source === 'product-form') {
            const variant = String(event.productVariantId || '');
            const pending = this.pendingAdds.get(variant);
            if (pending) {
              this.sessionAddedVariants.add(variant);
              this.setDrawerReturnFocus(pending.card);
              this.showAddedFeedback(pending.button);
              this.clearPending(variant);
              this.closeSheet(false);
              this.updateStickyAction();
            }
          }

          if (this.isFullCartSnapshot(event?.cartData)) this.applyCartSnapshot(event.cartData);
          else this.refreshCartState();
        });

        this.unsubscribeCartError = subscribe(PUB_SUB_EVENTS.cartError, (event) => {
          if (event?.source !== 'product-form') return;
          const variant = String(event.productVariantId || '');
          const pending = this.pendingAdds.get(variant);
          if (!pending) return;

          this.clearPending(variant);
          this.handleAddError(pending, event.errors || event.message || '');
        });
      }

      onStandardCartError(event) {
        const productForm = event.target?.closest?.('product-form');
        const form = productForm?.querySelector('[data-tora-card-form]');
        if (!form || !this.contains(form)) return;

        const pendingEntry = Array.from(this.pendingAdds.entries()).find(([, pending]) => pending.form === form);
        if (!pendingEntry) return;
        const [variant, pending] = pendingEntry;

        this.clearPending(variant);
        this.handleAddError(pending, event.error || '', event.code || '');
      }

      clearPending(variant) {
        const timer = this.pendingTimers.get(variant);
        if (timer) window.clearTimeout(timer);
        this.pendingTimers.delete(variant);
        this.pendingAdds.delete(variant);
      }

      handleAddError(pending, message, code = '') {
        const text = String(message || '');
        const inventoryError =
          /MAXIMUM_EXCEEDED|INVALID_MERCHANDISE_LINE|MERCHANDISE_NOT_APPLICABLE/i.test(String(code)) ||
          /sold out|unavailable|inventory|نفد|غير متاح/i.test(text);
        const card = pending.card;

        const wrapper = pending.form?.closest('product-form')?.querySelector('.product-form__error-message-wrapper');
        const errorNode = wrapper?.querySelector('.product-form__error-message');
        if (wrapper && errorNode) {
          errorNode.textContent =
            text ||
            (this.isArabic
              ? 'تعذر تحديث السلة. حاول مرة أخرى.'
              : 'The cart could not be updated. Please try again.');
          wrapper.hidden = false;
        }

        if (inventoryError) {
          const selectedRadio = card?.querySelector(`[data-tora-sheet-variant][value="${card.dataset.variantId}"]`);
          if (selectedRadio) {
            selectedRadio.disabled = true;
            selectedRadio.closest('label')?.classList.add('is-unavailable');
            const fallback = card.querySelector('[data-tora-sheet-variant]:not(:disabled)');
            if (fallback) {
              fallback.checked = true;
              fallback.dataset.toraAutomaticSelection = 'true';
              fallback.dispatchEvent(new Event('change', { bubbles: true }));
              this.announce(this.isArabic ? 'نفد الخيار المحدد، واخترنا أول خيار متوفر.' : 'That option sold out, so the first available option was selected.');
              return;
            }
          }

          pending.button?.setAttribute('disabled', 'disabled');
          const label = pending.button?.querySelector('span');
          if (label) label.textContent = this.isArabic ? 'نفدت الكمية' : 'Sold out';
          card?.setAttribute('data-unavailable', 'true');
          this.chooseAvailableCard(card);
          if (card && this.activeSheet?.closest('[data-tora-product-card]') === card) this.closeSheet(false);
          const cardSection = card?.closest('.tora-lp__top-section, .tora-lp__accessories');
          card?.remove();
          if (cardSection && !cardSection.querySelector('[data-tora-product-card]')) cardSection.remove();
        }

        this.announce(
          text ||
            (this.isArabic
              ? 'تعذر تحديث السلة. حاول مرة أخرى.'
              : 'The cart could not be updated. Please try again.')
        );
      }

      chooseAvailableCard(excludedCard) {
        const nextCard = Array.from(this.querySelectorAll('[data-tora-product-card]:not([data-unavailable="true"])')).find(
          (card) => card !== excludedCard && !card.hidden
        );
        this.updateStickyProduct(nextCard || null);
      }

      selectCard(card, source) {
        if (!card) return;
        this.updateStickyProduct(card);
        this.track('tora_offer_selected', this.cardPayload(card, source));
      }

      updateStickyProduct(card) {
        if (card?.dataset.unavailable === 'true') return;
        this.activeCard = card;
        if (this.stickyTitle) this.stickyTitle.textContent = card?.dataset.productTitle || '';
        if (this.stickyPrice) {
          this.stickyPrice.textContent = card
            ? card.dataset.priceFormatted || this.formatMoney(Number(card.dataset.price || 0))
            : '';
        }
        this.updateStickyAction();
        if (!card) this.syncStickyVisibility();
      }

      activeVariantWasAdded() {
        return Boolean(this.activeCard && this.sessionAddedVariants.has(String(this.activeCard.dataset.variantId || '')));
      }

      updateStickyAction() {
        if (!this.stickyButton) return;
        const showCart = this.activeVariantWasAdded();
        this.stickyButton.textContent = showCart
          ? this.stickyButton.dataset.cartLabel || (this.isArabic ? 'عرض السلة وإتمام الطلب' : 'View cart & checkout')
          : this.stickyAddLabel;
        this.stickyButton.dataset.toraStickyAction = showCart ? 'cart' : 'add';
        if (showCart) {
          this.stickyButton.setAttribute('aria-label', this.stickyButton.dataset.cartAriaLabel || (this.isArabic ? 'عرض السلة وإتمام الطلب' : 'View cart and checkout'));
        } else if (this.stickyAddAriaLabel) this.stickyButton.setAttribute('aria-label', this.stickyAddAriaLabel);
        else this.stickyButton.removeAttribute('aria-label');
        if (this.cartDrawer || this.activeCard?.dataset.directAdd !== 'true') this.stickyButton.setAttribute('aria-haspopup', 'dialog');
        else this.stickyButton.removeAttribute('aria-haspopup');
      }

      setDrawerReturnFocus(card) {
        const returnTarget = card?.querySelector(
          '[data-tora-options-trigger], [data-tora-add-button], [data-tora-card-link]'
        );
        if (returnTarget && typeof this.cartDrawer?.setActiveElement === 'function') {
          this.cartDrawer.setActiveElement(returnTarget);
        }
      }

      submitActiveCard(stickyTrigger) {
        const card = this.activeCard;
        if (!card) return false;

        if (card.dataset.directAdd === 'true') {
          const form = card.querySelector('[data-tora-card-form]');
          const button = form?.querySelector('[data-tora-add-button]');
          if (!form || button?.disabled || button?.getAttribute('aria-disabled') === 'true') return false;
          form.dataset.toraSubmitSource = 'sticky';
          form.requestSubmit();
          return true;
        }

        const sheet = card.querySelector('[data-tora-options-sheet]');
        if (!sheet) return false;
        this.openSheet(sheet, stickyTrigger);
        return true;
      }

      showAddedFeedback(button) {
        if (!button) return;
        const label = button.querySelector('span');
        if (!label) return;

        window.clearTimeout(button.toraFeedbackTimer);
        const defaultLabel = button.dataset.defaultFullLabel || label.textContent.trim();
        const addedLabel = button.dataset.addedLabel || (this.isArabic ? 'تمت الإضافة' : 'Added');
        button.classList.add('is-added');
        label.textContent = `${addedLabel} ✓`;
        this.announce(this.isArabic ? 'تمت إضافة المنتج إلى السلة' : 'Product added to cart');

        button.toraFeedbackTimer = window.setTimeout(() => {
          label.textContent = defaultLabel;
          button.classList.remove('is-added');
        }, 1800);
      }

      observeTopProducts() {
        if (!this.topProductsEnd || !('IntersectionObserver' in window)) {
          this.fallbackScrollHandler = () => {
            const rect = this.topProductsEnd?.getBoundingClientRect();
            this.topProductsPassed = Boolean(rect && rect.top < 48);
            this.syncStickyVisibility();
          };
          window.addEventListener('scroll', this.fallbackScrollHandler, {
            passive: true,
            signal: this.abortController.signal,
          });
          this.fallbackScrollHandler();
          return;
        }

        this.topObserver = new IntersectionObserver(
          ([entry]) => {
            this.topProductsPassed = !entry.isIntersecting && entry.boundingClientRect.top < 48;
            this.syncStickyVisibility();
          },
          { rootMargin: '-48px 0px 0px', threshold: 0 }
        );
        this.topObserver.observe(this.topProductsEnd);
      }

      observeCartDrawer() {
        if (!this.cartDrawer || !('MutationObserver' in window)) return;
        this.ensureCartAssurance();
        this.drawerObserver = new MutationObserver((mutations) => {
          this.syncStickyVisibility();
          this.ensureCartAssurance();
          if (!this.cartDrawerNeedsRefresh || this.pendingAdds.size) return;
          if (this.cartDrawer.classList.contains('active')) {
            if (mutations.some((mutation) => mutation.type === 'childList')) this.scheduleCartSnapshotRefresh();
          } else {
            window.clearTimeout(this.cartSnapshotTimer);
            this.cartSnapshotTimer = null;
            if (!this.cartReadController) this.refreshCartState({ includeDrawer: true });
          }
        });
        this.drawerObserver.observe(this.cartDrawer, { attributes: true, attributeFilter: ['class'], childList: true, subtree: true });
      }

      scheduleCartSnapshotRefresh() {
        window.clearTimeout(this.cartSnapshotTimer);
        const revision = this.cartStateRevision;
        this.cartSnapshotTimer = window.setTimeout(() => {
          this.cartSnapshotTimer = null;
          if (!this.isConnected || revision !== this.cartStateRevision || !this.cartDrawerNeedsRefresh || this.pendingAdds.size) return;
          this.refreshCartState();
        }, 160);
      }

      ensureCartAssurance() {
        this.removeCartCrossSell();
        if (!this.cartDrawer || this.cartDrawer.classList.contains('is-empty')) return;
        const footer = this.cartDrawer.querySelector('.drawer__footer');
        if (!footer || footer.querySelector('[data-tora-cart-assurance-content]')) return;
        const content = this.querySelector('template[data-tora-cart-assurance]')?.content.querySelector('[data-tora-cart-assurance-content]');
        const checkout = footer.querySelector('.cart__ctas');
        if (content && checkout) checkout.before(content.cloneNode(true));
      }

      removeCartCrossSell() {
        this.cartDrawer?.querySelectorAll('.cart-drawer__collection').forEach((element) => element.remove());
        this.cartDrawer?.querySelectorAll('.cart-drawer__warnings--has-collection').forEach((element) => {
          element.classList.remove('cart-drawer__warnings--has-collection');
        });
      }

      syncStickyVisibility() {
        const drawerOpen = this.cartDrawer?.classList.contains('active');
        const shouldShow = Boolean(
          this.activeCard && this.topProductsPassed && !this.activeSheet && !drawerOpen && !this.keyboardOpen
        );
        this.sticky?.toggleAttribute('hidden', !shouldShow);
        document.body.classList.toggle('tora-products-passed', this.topProductsPassed);
        this.positionFloatingWidgets();
      }

      openSheet(sheet, trigger) {
        if (!sheet || this.activeSheet === sheet) return;
        if (this.activeSheet) this.closeSheet(false);

        this.activeSheet = sheet;
        this.activeSheetTrigger = trigger;
        sheet.hidden = false;
        sheet.setAttribute('dir', this.getAttribute('dir') || 'ltr');
        trigger?.setAttribute('aria-expanded', 'true');
        document.body.classList.add('tora-options-open');
        this.syncStickyVisibility();
        const source = trigger === this.sticky?.querySelector('[data-tora-sticky-add]') ? 'sticky' : 'card';
        sheet.dataset.toraOpenSource = source;
        this.track('tora_bottom_sheet_opened', this.cardPayload(sheet.closest('[data-tora-product-card]'), source));

        window.requestAnimationFrame(() => {
          const firstAvailable = sheet.querySelector('[data-tora-sheet-variant]:checked:not(:disabled)') || sheet.querySelector('[data-tora-sheet-variant]:not(:disabled)');
          const close = sheet.querySelector('.tora-options__close');
          (firstAvailable || close || sheet.querySelector('[role="dialog"]'))?.focus();
        });
      }

      closeSheet(restoreFocus = true) {
        if (!this.activeSheet) return;
        const trigger = this.activeSheetTrigger;
        this.activeSheet.hidden = true;
        delete this.activeSheet.dataset.toraOpenSource;
        this.activeSheet = null;
        this.activeSheetTrigger = null;
        trigger?.setAttribute('aria-expanded', 'false');
        document.body.classList.remove('tora-options-open');
        this.syncStickyVisibility();
        if (restoreFocus) trigger?.focus();
      }

      onDocumentKeydown(event) {
        if (!this.activeSheet) return;
        if (event.key === 'Escape') {
          event.preventDefault();
          this.closeSheet();
          return;
        }
        if (event.key !== 'Tab') return;

        const dialog = this.activeSheet.querySelector('[role="dialog"]');
        const focusable = Array.from(
          dialog.querySelectorAll('button:not(:disabled), input:not(:disabled), a[href], [tabindex]:not([tabindex="-1"])')
        );
        if (!focusable.length) return;
        const first = focusable[0];
        const last = focusable[focusable.length - 1];
        if (event.shiftKey && document.activeElement === first) {
          event.preventDefault();
          last.focus();
        } else if (!event.shiftKey && document.activeElement === last) {
          event.preventDefault();
          first.focus();
        }
      }

      observeFloatingWidgets() {
        this.findFloatingWidgets();
        if (!('MutationObserver' in window)) return;
        this.widgetObserver = new MutationObserver(() => {
          window.clearTimeout(this.widgetTimer);
          this.widgetTimer = window.setTimeout(() => this.findFloatingWidgets(), 120);
        });
        this.widgetObserver.observe(document.body, { childList: true, subtree: true });
      }

      findFloatingWidgets() {
        const candidates = document.querySelectorAll(
          'img[src*="cdn.live.zoko.io"], img[src*="widget_icon"], [id*="zoko" i], [class*="zoko" i], a[href*="wa.me"], a[href*="whatsapp"]'
        );

        candidates.forEach((candidate) => {
          if (candidate.closest('tora-offers-landing, cart-drawer')) return;
          let container = candidate;
          let depth = 0;
          while (container.parentElement && container.parentElement !== document.body && depth < 8) {
            if (getComputedStyle(container).position === 'fixed') break;
            container = container.parentElement;
            depth += 1;
          }
          if (getComputedStyle(container).position !== 'fixed') return;
          if (!this.floatingWidgets.has(container)) {
            this.floatingWidgets.set(container, {
              bottom: container.style.bottom,
              zIndex: container.style.zIndex,
            });
            container.classList.add('tora-floating-widget');
          }
        });

        this.positionFloatingWidgets();
      }

      positionFloatingWidgets() {
        // Inline support stays available without covering catalog controls.
        // Some widgets have zero-height fixed containers but visible children.
        this.floatingWidgets.forEach((_original, widget) => {
          widget.classList.add('tora-floating-widget--suppressed');
        });
      }

      restoreFloatingWidgets() {
        this.floatingWidgets.forEach((original, widget) => {
          widget.classList.remove('tora-floating-widget', 'tora-floating-widget--suppressed');
          widget.style.bottom = original.bottom;
          widget.style.zIndex = original.zIndex;
        });
        this.floatingWidgets.clear();
      }

      prepareNativeCartTarget() {
        if (!this.cartDrawer || document.getElementById('cart-icon-bubble')) return;
        // Dawn's native quantity renderer requires this section target. TORA's
        // custom header has its own visible count, synchronized from cartUpdate.
        // Keep a hidden target so successful edits reach that event without a
        // missing-element exception; never replace an existing theme target.
        this.nativeCartTarget = document.createElement('div');
        this.nativeCartTarget.id = 'cart-icon-bubble';
        this.nativeCartTarget.hidden = true;
        this.nativeCartTarget.setAttribute('aria-hidden', 'true');
        document.body.append(this.nativeCartTarget);
      }

      prepareHeaderCartLinks() {
        if (typeof this.cartDrawer?.open !== 'function') return;
        document.querySelectorAll('.tora-nav__cart').forEach((link) => {
          link.setAttribute('aria-haspopup', 'dialog');
          link.setAttribute('aria-controls', 'CartDrawer');
        });
      }

      onHeaderCartClick(event) {
        const link = event.target.closest('.tora-nav__cart');
        if (!link || event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
        if (typeof this.cartDrawer?.open !== 'function') return;
        event.preventDefault();
        this.openCart(link);
      }

      onCartInteraction(event) {
        if (!event.target.closest('cart-drawer')) return;
        if (event.type === 'click' && !event.target.closest('cart-remove-button, .quantity__button')) return;
        this.invalidateCartRead();
      }

      invalidateCartRead() {
        this.cartStateRevision += 1;
        this.cartDrawerNeedsRefresh = true;
        window.clearTimeout(this.cartSnapshotTimer);
        this.cartSnapshotTimer = null;
        this.cartReadController?.abort();
        this.cartReadController = null;
      }

      getCartUrl() {
        return window.routes?.cart_url || `${window.Shopify?.routes?.root || '/'}cart`;
      }

      isFullCartSnapshot(cart) {
        return Boolean(cart && Number.isFinite(cart.item_count) && Array.isArray(cart.items));
      }

      applyCartSnapshot(cart) {
        if (!this.isFullCartSnapshot(cart)) return;
        const cartVariants = new Set(cart.items.filter((item) => Number(item.quantity) > 0).map((item) => String(item.variant_id || item.id)));
        this.sessionAddedVariants.forEach((variant) => {
          if (!cartVariants.has(variant)) this.sessionAddedVariants.delete(variant);
        });
        this.updateStickyAction();

        const customCartLinks = document.querySelectorAll('.tora-nav__cart');
        customCartLinks.forEach((link) => {
          let countNode = link.querySelector('.tora-nav__cart-count');
          if (cart.item_count > 0 && !countNode) {
            countNode = document.createElement('span');
            countNode.className = 'tora-nav__cart-count';
            countNode.setAttribute('aria-hidden', 'true');
            link.append(countNode);
          }
          if (countNode) {
            if (cart.item_count > 0) countNode.textContent = cart.item_count > 99 ? '99+' : String(cart.item_count);
            else countNode.remove();
          }
          link.setAttribute('aria-label', this.isArabic ? `عرض السلة (${cart.item_count})` : `View cart (${cart.item_count})`);
        });
      }

      async refreshCartState({ includeDrawer = false } = {}) {
        this.cartReadController?.abort();
        const controller = new AbortController();
        this.cartReadController = controller;
        const revision = this.cartStateRevision;
        const timeout = window.setTimeout(() => controller.abort(), 6000);
        const canRefreshDrawer = includeDrawer && this.cartDrawer && !this.cartDrawer.classList.contains('active') && !this.pendingAdds.size;
        const options = {
          headers: { Accept: 'application/json' },
          credentials: 'same-origin',
          cache: 'no-store',
          signal: controller.signal,
        };

        try {
          const sectionUrl = new URL(this.getCartUrl(), window.location.origin);
          sectionUrl.searchParams.set('sections', 'cart-drawer');
          const readJson = async (url) => {
            const response = await fetch(url, options);
            if (!response.ok) throw new Error('Cart refresh failed');
            return response.json();
          };
          const [cart, sections] = await Promise.all([
            readJson(`${this.getCartUrl()}.js`),
            canRefreshDrawer ? readJson(sectionUrl.href) : Promise.resolve(null),
          ]);
          if (controller.signal.aborted || revision !== this.cartStateRevision || !this.isConnected) return null;
          if (!this.isFullCartSnapshot(cart)) return null;
          this.applyCartSnapshot(cart);
          let drawerUpdated = false;
          if (sections && !this.cartDrawer.classList.contains('active') && !this.pendingAdds.size) {
            drawerUpdated = this.renderClosedCartDrawer(sections['cart-drawer'], cart);
          }
          return { cart, drawerUpdated };
        } catch (_error) {
          return null;
        } finally {
          window.clearTimeout(timeout);
          if (this.cartReadController === controller) this.cartReadController = null;
        }
      }

      renderClosedCartDrawer(html, cart) {
        if (!html || !this.cartDrawer || this.cartDrawer.classList.contains('active')) return false;
        const source = new DOMParser().parseFromString(html, 'text/html');
        const sourceDrawer = source.querySelector('cart-drawer');
        const sourceContent = source.querySelector('#CartDrawer');
        const target = this.cartDrawer.querySelector('#CartDrawer');
        if (!sourceDrawer || !sourceContent?.querySelector('.drawer__inner') || !target) return false;
        if (sourceDrawer.classList.contains('is-empty') !== (cart.item_count === 0)) return false;
        target.innerHTML = sourceContent.innerHTML;
        this.cartDrawer.classList.toggle('is-empty', cart.item_count === 0);
        this.cartDrawer.querySelector('#CartDrawer-Overlay')?.addEventListener('click', this.cartDrawer.close.bind(this.cartDrawer));
        this.cartDrawerNeedsRefresh = false;
        return true;
      }

      async openCart(trigger) {
        if (this.openingCart || this.cartDrawer?.classList.contains('active')) return;
        if (this.pendingAdds.size) {
          if (Array.from(this.pendingAdds.values()).some((pending) => pending.timedOut)) {
            window.location.assign(this.getCartUrl());
            return;
          }
          this.announce(this.isArabic ? 'السلة قيد التحديث، انتظر لحظة.' : 'Your cart is updating. Please wait a moment.');
          return;
        }
        if (typeof this.cartDrawer?.open !== 'function') {
          window.location.assign(this.getCartUrl());
          return;
        }
        this.openingCart = true;
        const revision = this.cartStateRevision;
        trigger?.setAttribute('aria-busy', 'true');
        this.closeSheet(false);
        try {
          if (!this.cartDrawerNeedsRefresh) {
            this.ensureCartAssurance();
            this.cartDrawer.open(trigger);
            return;
          }
          const result = await this.refreshCartState({ includeDrawer: true });
          if (!this.isConnected || revision !== this.cartStateRevision || this.pendingAdds.size || this.cartDrawer.classList.contains('active')) return;
          if (!result?.drawerUpdated) {
            window.location.assign(this.getCartUrl());
            return;
          }
          this.ensureCartAssurance();
          this.cartDrawer.open(trigger);
        } catch (_error) {
          if (this.isConnected && revision === this.cartStateRevision && !this.cartDrawer?.classList.contains('active')) {
            window.location.assign(this.getCartUrl());
          }
        } finally {
          trigger?.removeAttribute('aria-busy');
          this.openingCart = false;
        }
      }

      retargetHeaderCtas() {
        const openingId = this.querySelector('.tora-lp__opening')?.id;
        if (!openingId) return;
        document.querySelectorAll('.tora-nav__cta').forEach((link) => {
          link.href = `#${openingId}`;
        });
      }

      formatMoney(cents) {
        const value = Number(cents || 0) / 100;
        const number = new Intl.NumberFormat(this.locale, {
          maximumFractionDigits: Number(cents || 0) % 100 === 0 ? 0 : 2,
        }).format(value);
        if (this.currency === 'SAR') return this.isArabic ? `${number} ر.س` : `SAR ${number}`;
        try {
          return new Intl.NumberFormat(this.locale, {
            style: 'currency',
            currency: this.currency,
            maximumFractionDigits: Number(cents || 0) % 100 === 0 ? 0 : 2,
          }).format(value);
        } catch (_error) {
          return `${number} ${this.currency}`;
        }
      }

      cardPayload(card, source = 'card') {
        if (!card) return { landing_version: this.version, currency: this.currency, source };
        return {
          product_id: String(card.dataset.productId || ''),
          product_handle: card.dataset.productHandle || '',
          product_name: card.dataset.productTitle || '',
          variant: String(card.dataset.variantId || ''),
          group: card.dataset.cardPlacement || '',
          card_position: Number(card.dataset.cardPosition || 0),
          pack_count: Number(card.dataset.packCount || 1),
          price: Number(card.dataset.price || 0) / 100,
          currency: this.currency,
          landing_version: this.version,
          source,
        };
      }

      track(eventName, payload = {}) {
        window.dataLayer = window.dataLayer || [];
        window.dataLayer.push({ event: eventName, ...payload });
      }

      announce(message) {
        if (!this.announcement) return;
        this.announcement.textContent = '';
        window.requestAnimationFrame(() => {
          this.announcement.textContent = message;
        });
      }
    }
  );
}
